import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { imgSrc } from "@/lib/img";
import MapLeaflet from "@/components/MapLeaflet";
import { CollectiveTypeBadge } from "@/components/CollectiveTypeBadge";
import { CollectiveChat } from "@/components/collectives/CollectiveChat";
import { CalendarSubscribeLink } from "@/components/CalendarSubscribe";
import { ConflictWarningDialog } from "@/components/ConflictWarningDialog";
import { Seo } from "@/components/Seo";
import { TierBadge } from "@/components/TierBadge";
import {
  MapPinned, Users, Crown, Send, Trash2, Lock, ArrowLeft, MapPin, Clock,
  Megaphone, MessagesSquare, HeartHandshake, Sprout, BarChart3, Coffee, Plus, CheckCircle2, Flame,
  CalendarPlus, MessageCircle, ExternalLink, ShieldAlert, QrCode, ShieldCheck, Lightbulb, Printer,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ShareButton } from "@/components/ShareButton";
import { ReportButton } from "@/components/ReportButton";
import { RoleBadge } from "@/components/RoleBadge";
import { openWhatsApp } from "@/lib/share";
import { toast } from "sonner";

function Noticeboard({ id, isLeader, userId }) {
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const load = () => api.collectivePosts(id).then(setPosts).catch(() => {});
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps -- render-scoped fetch helper; effect keys on its real inputs

  const post = async () => {
    if (!text.trim()) return;
    try {
      const p = await api.createCollectivePost(id, text.trim());
      setPosts((list) => [p, ...list]);
      setText("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post"); }
  };
  const remove = async (postId) => {
    try {
      await api.deleteCollectivePost(id, postId);
      setPosts((list) => list.filter(p => p.id !== postId));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div data-testid="collective-noticeboard">
      <div className="flex gap-2">
        <Textarea data-testid="noticeboard-input" value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Share an update with the collective…" className="flex-1" />
        <Button data-testid="noticeboard-post-btn" onClick={post} className="rounded-full bg-forest hover:bg-forest-hover self-end h-10 px-4">
          <Send className="w-4 h-4" />
        </Button>
      </div>
      <div className="mt-5 space-y-4">
        {posts.length === 0 && <div className="text-sm text-neutral-500">No posts yet — start the conversation!</div>}
        {posts.map((p) => (
          <div key={p.id} data-testid={`noticeboard-post-${p.id}`} className="bg-white border border-neutral-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Avatar className="w-9 h-9 shrink-0">
                <AvatarImage src={imgSrc(p.picture)} alt={p.user_name} />
                <AvatarFallback className="bg-forest text-white text-xs">{p.user_name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="text-sm">
                  <Link to={`/u/${p.user_id}`} className="font-medium text-forest hover:underline">{p.user_name}</Link>
                  {p.is_leader && <Badge className="ml-2 rounded-full bg-terracotta text-white border-none text-[10px]"><Crown className="w-2.5 h-2.5 mr-0.5" /> Leader</Badge>}
                  {p.role_badge && <RoleBadge badge={p.role_badge} className="ml-1.5" />}
                  <span className="text-xs text-neutral-400 ml-2">{new Date(p.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-sm text-neutral-700 mt-1 whitespace-pre-line">{p.content}</p>
                {p.kind === "challenge" && p.area && (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/og/area/${encodeURIComponent(p.area)}`}
                    alt={`${p.area} neighbourhood challenge card`}
                    data-testid={`noticeboard-challenge-card-${p.id}`}
                    loading="lazy"
                    className="mt-2 w-full max-w-md rounded-xl border border-forest/10"
                  />
                )}
                {p.kind === "achievement" && p.achievement_id && (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/og/achievement/${p.achievement_user_id}/${p.achievement_id}`}
                    alt="Achievement card"
                    data-testid={`noticeboard-achievement-card-${p.id}`}
                    loading="lazy"
                    className="mt-2 w-full max-w-md rounded-xl border border-forest/10"
                  />
                )}
                {p.kind === "challenge" && p.link && (
                  <Link
                    to={p.link}
                    data-testid={`noticeboard-challenge-link-${p.id}`}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-terracotta hover:underline"
                  >
                    <Flame className="w-3.5 h-3.5" /> See the Areas leaderboard
                  </Link>
                )}
              </div>
              {p.user_id !== userId && (
                <ReportButton targetType="collective_post" targetId={p.id} label="" className="shrink-0" />
              )}
              {(p.user_id === userId || isLeader) && (
                <button data-testid={`noticeboard-delete-${p.id}`} onClick={() => remove(p.id)} className="text-neutral-400 hover:text-red-600 transition-colors shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OppRow({ o, hostedByThis }) {
  return (
    <Link
      to={`/opportunities/${o.id}`}
      data-testid={`local-item-${o.id}`}
      className="block bg-white border border-neutral-200 rounded-xl p-4 hover:border-forest/40 transition-colors"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-medium text-forest flex items-center gap-2 flex-wrap">
            {o.title}
            {hostedByThis && (
              <Badge data-testid={`hosted-badge-${o.id}`} className="rounded-full bg-terracotta text-white border-none text-[10px]">
                <MapPinned className="w-2.5 h-2.5 mr-1" /> Hosted by this collective
              </Badge>
            )}
          </div>
          <div className="text-xs text-neutral-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{o.location}</span>
            {o.event_date && (
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(o.event_date).toLocaleDateString()}</span>
            )}
            {typeof o.distance_miles === "number" && <span className="text-forest font-medium">{o.distance_miles} mi</span>}
          </div>
        </div>
        {o.is_help_request ? (
          <Badge className="rounded-full bg-sky-600 text-white border-none text-[10px]"><HeartHandshake className="w-2.5 h-2.5 mr-1" /> Help request</Badge>
        ) : o.community_led ? (
          <Badge className="rounded-full bg-terracotta text-white border-none text-[10px]"><Sprout className="w-2.5 h-2.5 mr-1" /> Community event</Badge>
        ) : (
          <Badge className="rounded-full bg-forest/10 text-forest border-none text-[10px]">Opportunity</Badge>
        )}
      </div>
    </Link>
  );
}

function HostCta({ id, isMember }) {
  // Post-Phase-10 IA: the old copy pointed at a "New opportunity" page that
  // no longer exists in the nav — members get a direct CTA (with this
  // collective preselected via ?collective=), non-members a join prompt.
  return isMember ? (
    <Link
      to={`/post-opportunity?collective=${id}`}
      data-testid="collective-host-cta"
      className="inline-flex items-center gap-1.5 mt-2 text-sm font-medium rounded-full bg-terracotta text-white px-4 py-2 hover:bg-terracotta-hover transition-colors"
    >
      <Sprout className="w-4 h-4" /> Host something under this collective
    </Link>
  ) : (
    <div className="text-xs text-neutral-500 mt-1" data-testid="collective-host-join-hint">
      Join this collective to host events under its name.
    </div>
  );
}

function LocalActivity({ id, isMember }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.collectiveLocal(id).then(setData).catch(() => setData({ hosted: [], nearby: [] })); }, [id]);
  if (!data) return <div className="text-sm text-neutral-500">Loading…</div>;
  const hosted = data.hosted || [];
  const nearby = data.nearby || [];
  if (hosted.length === 0 && nearby.length === 0) {
    return (
      <div>
        <div className="text-sm text-neutral-500">Nothing happening in this area yet — why not host something?</div>
        <HostCta id={id} isMember={isMember} />
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <section data-testid="collective-hosted-section">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] uppercase tracking-[0.18em] text-terracotta/80 font-bold flex items-center gap-1.5">
            <MapPinned className="w-3 h-3" /> Hosted by this collective
          </div>
          {/* persistent host entry point — the empty-state CTA below vanishes
              once the first event exists, members must still have a way in */}
          {isMember && (
            <Link
              to={`/post-opportunity?collective=${id}`}
              data-testid="collective-host-btn"
              className="inline-flex items-center gap-1 text-xs font-semibold rounded-full border border-terracotta/40 text-terracotta px-3 py-1.5 hover:bg-terracotta hover:text-white transition-colors"
            >
              <Sprout className="w-3.5 h-3.5" /> Host
            </Link>
          )}
        </div>
        {hosted.length === 0 ? (
          <div className="mt-2">
            <div className="text-sm text-neutral-500">Nothing hosted by this collective yet.</div>
            <HostCta id={id} isMember={isMember} />
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            {hosted.map((o) => <OppRow key={o.id} o={o} hostedByThis />)}
          </div>
        )}
      </section>
      <section data-testid="collective-nearby-section">
        <div className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold flex items-center gap-1.5">
          <MapPin className="w-3 h-3" /> Nearby in the area
        </div>
        {nearby.length === 0 ? (
          <div className="mt-2 text-sm text-neutral-500">Nothing nearby right now.</div>
        ) : (
          <div className="mt-3 space-y-3">
            {nearby.map((o) => <OppRow key={o.id} o={o} />)}
          </div>
        )}
      </section>
    </div>
  );
}

function StatsTab({ id }) {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.collectiveStats(id).then(setStats).catch((e) => setErr(e?.response?.data?.detail || "Could not load stats")); }, [id]);
  if (err) return <div data-testid="stats-error" className="text-sm text-red-600">{err}</div>;
  if (!stats) return <div className="text-sm text-neutral-500">Loading…</div>;
  const growth = stats.member_growth || [];
  const maxCount = Math.max(1, ...growth.map((p) => p.count));
  const width = 560;
  const height = 96;
  const pad = 8;
  const step = (width - pad * 2) / Math.max(1, growth.length - 1);
  const points = growth.map((p, i) => {
    const x = pad + i * step;
    const y = height - pad - (p.count / maxCount) * (height - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <div data-testid="stats-tab" className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div data-testid="stat-members" className="bg-white border border-neutral-200 rounded-xl p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold">Members</div>
          <div className="font-display text-3xl text-forest mt-2">{stats.member_count}</div>
        </div>
        <div data-testid="stat-active-30d" className="bg-white border border-neutral-200 rounded-xl p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold">Active · last 30d</div>
          <div className="font-display text-3xl text-forest mt-2">{stats.active_in_last_30d}</div>
        </div>
        <div data-testid="stat-hours" className="bg-white border border-neutral-200 rounded-xl p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold">Hours logged</div>
          <div className="font-display text-3xl text-forest mt-2">{stats.hours_logged.all_time}h</div>
          <div className="text-xs text-neutral-500 mt-1">{stats.hours_logged.last_30d}h in last 30 days</div>
        </div>
      </div>
      <div className="bg-white border border-neutral-200 rounded-xl p-5">
        <div className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold">Member growth · 30 days</div>
        <svg data-testid="stat-growth-chart" viewBox={`0 0 ${width} ${height}`} className="w-full mt-3">
          <polyline points={points} fill="none" stroke="#1A4331" strokeWidth="2" />
          <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} stroke="#E5E7EB" />
        </svg>
        <div className="flex justify-between text-[10px] text-neutral-400 mt-1">
          <span>{growth[0]?.date}</span>
          <span>{growth[growth.length - 1]?.date}</span>
        </div>
      </div>
      <div data-testid="stat-events" className="bg-white border border-neutral-200 rounded-xl p-5 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold">Events hosted</div>
          <div className="font-display text-2xl text-forest mt-1">{stats.events_hosted}</div>
        </div>
        <div className="text-xs text-neutral-500">See them in <span className="font-medium text-forest">Local activity</span>.</div>
      </div>
    </div>
  );
}

function InviteCard({ id }) {
  const [invite, setInvite] = useState(null);
  useEffect(() => { api.collectiveInvite(id).then(setInvite).catch(() => setInvite(null)); }, [id]);
  const copy = async () => {
    if (!invite?.invite_url) return;
    try {
      await navigator.clipboard.writeText(invite.invite_url);
      toast.success("Invite link copied");
    } catch { toast("Copy failed — long-press the field to select"); }
  };
  if (!invite) return null;
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">
      <div className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold">Leader invite link</div>
      <p className="text-xs text-neutral-500">Share this with neighbours you want in the collective. They still need to be inside the radius.</p>
      <div className="flex gap-2 items-center">
        <Input data-testid="collective-invite-url" value={invite.invite_url} readOnly className="text-xs h-9" />
        <Button data-testid="collective-invite-copy-btn" onClick={copy} variant="outline" className="rounded-full h-9 px-4 text-xs">Copy</Button>
      </div>
    </div>
  );
}

// 17-C item 6: "Chat on WhatsApp" behind an interstitial — external-link
// safety note + a report path. v1 is entirely link-based (no WhatsApp API).
function WhatsAppChatCard({ c, user }) {
  const [open, setOpen] = useState(false);
  const [reported, setReported] = useState(false);
  if (!c.whatsapp_link) return null;
  const report = async () => {
    try {
      await api.report("collective", c.id, "Suspicious WhatsApp link", c.whatsapp_link);
      setReported(true);
      toast.success("Thanks — an admin will take a look.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the report");
    }
  };
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5" data-testid="whatsapp-chat-card">
      <div className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold">Group chat</div>
      <Button
        data-testid="whatsapp-chat-btn"
        onClick={() => setOpen(true)}
        variant="outline"
        className="w-full rounded-full h-10 mt-3 border-[#25D366]/50 text-[#128C7E] hover:bg-[#25D366]/10 hover:text-[#128C7E]"
      >
        <MessageCircle className="w-4 h-4 mr-2" /> Chat on WhatsApp
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="whatsapp-interstitial" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-forest">You're leaving CivicHearth</DialogTitle>
            <DialogDescription>
              This WhatsApp group is run by the collective's leaders, not by CivicHearth.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-neutral-600 bg-cream/70 border border-neutral-200 rounded-xl px-4 py-3 flex items-start gap-2">
            <ShieldAlert className="w-4 h-4 text-terracotta shrink-0 mt-0.5" />
            <span>
              Joining shares your phone number with everyone in the group. Never send money or personal
              details to people you haven't met — our <Link to="/safety" className="underline text-forest">safety guidelines</Link> apply out there too.
            </span>
          </div>
          <div className="flex flex-col gap-2 mt-1">
            <a
              data-testid="whatsapp-interstitial-continue"
              href={c.whatsapp_link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[#25D366] text-white text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <ExternalLink className="w-4 h-4" /> Continue to WhatsApp
            </a>
            {user ? (
              <button
                data-testid="whatsapp-interstitial-report"
                onClick={report}
                disabled={reported}
                className="text-xs text-neutral-500 hover:text-red-600 underline disabled:no-underline disabled:text-neutral-400"
              >
                {reported ? "Reported — thank you" : "Something wrong with this link? Report it"}
              </button>
            ) : (
              <p className="text-[11px] text-neutral-400 text-center">Sign in to report a suspicious link.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// 18-B1 item 8: leader card — the intergenerational programme kit. Each
// format links to its printable A5 how-to; "Start from a template" in the
// New-gathering dialog prefills the form.
function ProgrammeIdeasCard() {
  const [templates, setTemplates] = useState([]);
  useEffect(() => { api.gatheringTemplates().then(setTemplates).catch(() => {}); }, []);
  if (!templates.length) return null;
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3" data-testid="programme-ideas-card">
      <div className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold inline-flex items-center gap-1.5">
        <Lightbulb className="w-3.5 h-3.5 text-terracotta" /> Programme ideas
      </div>
      <p className="text-xs text-neutral-500">
        Five ready-made intergenerational formats. Pick one under "Start from a template" when you post a gathering — or print the how-to first.
      </p>
      <div className="space-y-2">
        {templates.map((t) => (
          <div key={t.slug} className="flex items-center justify-between gap-2" data-testid={`programme-idea-${t.slug}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium text-forest">{t.title}</div>
              <div className="text-xs text-neutral-500 truncate">{t.tagline}</div>
            </div>
            <Link
              to={`/programme-guides/${t.slug}`}
              data-testid={`programme-guide-link-${t.slug}`}
              className="shrink-0 inline-flex items-center gap-1 text-xs px-3 h-8 rounded-full border border-forest/30 text-forest hover:bg-forest/5 transition-colors"
            >
              <Printer className="w-3 h-3" /> Guide
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}

// 17-C item 6: leader tool — paste-ready WhatsApp message + printable QR poster.
function InviteStreetCard({ c }) {
  const message =
    `Hi neighbours! Our street now has its own collective on CivicHearth — ${c.name} (${c.area_label}). ` +
    `Gatherings, a noticeboard, and a hand when you need one. Join us here: ${api.shareUrl("collective", c.id)}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Message copied — paste it into your street's WhatsApp group");
    } catch { toast("Copy failed — long-press the text to select it"); }
  };
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3" data-testid="invite-street-card">
      <div className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold">Invite your street</div>
      <p className="text-xs text-neutral-500">A ready-to-paste message for your street's WhatsApp group — the link unfurls with your collective's card.</p>
      <Textarea data-testid="invite-street-message" readOnly value={message} className="text-xs h-24 bg-cream/50" />
      <div className="flex flex-wrap gap-2">
        <Button data-testid="invite-street-copy" onClick={copy} variant="outline" className="rounded-full h-9 px-4 text-xs">Copy message</Button>
        <Button
          data-testid="invite-street-whatsapp"
          onClick={() => openWhatsApp(message)}
          className="rounded-full h-9 px-4 text-xs bg-[#25D366] hover:bg-[#1eb857] text-white"
        >
          <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Open WhatsApp
        </Button>
        <Link
          to={`/collectives/${c.id}/poster`}
          data-testid="invite-street-poster-link"
          className="inline-flex items-center gap-1.5 text-xs px-4 h-9 rounded-full border border-forest/30 text-forest hover:bg-forest/5 transition-colors"
        >
          <QrCode className="w-3.5 h-3.5" /> Print A5 QR poster
        </Link>
      </div>
    </div>
  );
}

function PendingRequests({ id, reload }) {
  const [items, setItems] = useState([]);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const load = () => api.collectivePending(id).then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps -- render-scoped fetch helper; effect keys on its real inputs
  const approve = async (u) => {
    try {
      await api.approvePending(id, u.user_id);
      toast.success(`${u.name} approved`);
      setItems((list) => list.filter((x) => x.user_id !== u.user_id));
      reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not approve"); }
  };
  const doReject = async () => {
    try {
      await api.rejectPending(id, rejecting.user_id, reason);
      toast(`${rejecting.name} rejected`);
      setItems((list) => list.filter((x) => x.user_id !== rejecting.user_id));
      setRejecting(null); setReason("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reject"); }
  };
  if (items.length === 0) return null;
  return (
    <div data-testid="pending-requests-section" className="bg-amber-50 border border-amber-200 rounded-xl p-5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-amber-900 font-bold">Pending requests ({items.length})</div>
      <div className="mt-3 space-y-2">
        {items.map((u) => (
          <div key={u.user_id} className="flex items-center gap-3 bg-white border border-amber-100 rounded-lg p-3">
            <Avatar className="w-9 h-9 shrink-0">
              <AvatarImage src={imgSrc(u.picture)} alt={u.name} />
              <AvatarFallback className="bg-forest text-white text-xs">{u.name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-forest">{u.name}</div>
              <div className="text-xs text-neutral-500">{u.location || "—"}</div>
            </div>
            <button data-testid={`pending-approve-${u.user_id}`} onClick={() => approve(u)} className="text-xs px-3 py-1.5 rounded-full bg-forest text-white hover:bg-forest-hover">Approve</button>
            <button data-testid={`pending-reject-${u.user_id}`} onClick={() => setRejecting(u)} className="text-xs px-3 py-1.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50">Reject</button>
          </div>
        ))}
      </div>
      {rejecting && (
        <div className="mt-4 bg-white border border-neutral-200 rounded-lg p-3">
          <div className="text-xs text-neutral-600">Reject <span className="font-medium text-forest">{rejecting.name}</span>?</div>
          <Textarea data-testid="pending-reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Optional reason (visible to the applicant)" className="mt-2 text-sm" />
          <div className="flex gap-2 mt-2 justify-end">
            <button onClick={() => { setRejecting(null); setReason(""); }} className="text-xs px-3 py-1.5 rounded-full border border-neutral-200 text-neutral-600">Cancel</button>
            <button onClick={doReject} className="text-xs px-3 py-1.5 rounded-full bg-red-600 text-white hover:bg-red-700">Confirm reject</button>
          </div>
        </div>
      )}
    </div>
  );
}

function MembersList({ c, isLeader, userId, reload }) {
  // 18-B1 item 5: leader vouch — an explicit trust signal (L2). Any leader's
  // vouch counts; the roster shows which members I have vouched for.
  const [vouches, setVouches] = useState(null);
  useEffect(() => {
    if (isLeader) api.collectiveVouches(c.id).then(setVouches).catch(() => {});
  }, [isLeader, c.id]);

  const toggleVouch = async (m) => {
    const mine = vouches?.my_vouched_user_ids?.includes(m.user_id);
    try {
      if (mine) {
        await api.unvouchMember(c.id, m.user_id);
        toast(`Vouch for ${m.name} removed`);
      } else {
        await api.vouchMember(c.id, m.user_id);
        toast.success(`You vouched for ${m.name}`);
      }
      setVouches(await api.collectiveVouches(c.id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update vouch"); }
  };

  const promote = async (m) => {
    try {
      await api.promoteCollectiveMember(c.id, m.user_id);
      toast.success(`${m.name} is now a leader`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not promote"); }
  };
  const demote = async (m) => {
    try {
      await api.demoteCollectiveMember(c.id, m.user_id);
      toast(`${m.name} is now a member`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change role"); }
  };
  const remove = async (m) => {
    try {
      await api.removeCollectiveMember(c.id, m.user_id);
      toast(`${m.name} removed`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };
  return (
    <div className="space-y-2" data-testid="collective-members-list">
      {c.members.map((m) => (
        <div key={m.user_id} data-testid={`member-row-${m.user_id}`} className="flex items-center gap-3 bg-white border border-neutral-200 rounded-xl p-3">
          <Avatar className="w-9 h-9 shrink-0">
            <AvatarImage src={imgSrc(m.picture)} alt={m.name} />
            <AvatarFallback className="bg-forest text-white text-xs">{m.name?.[0]}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <Link to={`/u/${m.user_id}`} className="text-sm font-medium text-forest hover:underline">{m.name}</Link>
            <TierBadge hours={m.total_hours} className="ml-1.5" />
            <div className="text-xs text-neutral-500">{m.location || "—"}</div>
          </div>
          {m.is_leader && <Badge className="rounded-full bg-terracotta text-white border-none text-[10px]"><Crown className="w-2.5 h-2.5 mr-0.5" /> Leader</Badge>}
          {isLeader && m.user_id !== userId && (
            <div className="flex gap-1.5">
              <button
                data-testid={`member-vouch-${m.user_id}`}
                onClick={() => toggleVouch(m)}
                title="A leader vouch raises this neighbour's trust level"
                className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1 ${
                  vouches?.my_vouched_user_ids?.includes(m.user_id)
                    ? "border-sky-300 bg-sky-50 text-sky-800"
                    : "border-neutral-200 text-neutral-600 hover:border-sky-400 hover:text-sky-700"
                }`}
              >
                <ShieldCheck className="w-3 h-3" />
                {vouches?.my_vouched_user_ids?.includes(m.user_id) ? "Vouched" : "Vouch"}
              </button>
              {!m.is_leader && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`member-promote-${m.user_id}`}
                      className="text-xs px-2.5 py-1.5 rounded-full border border-neutral-200 text-neutral-600 hover:border-forest hover:text-forest transition-colors">
                      Make leader
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid={`promote-confirm-${m.user_id}`}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Make {m.name} a leader?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Leaders can edit the collective, manage and remove members, approve join
                        requests, pin messages, manage chat rooms and attach the WhatsApp link.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid={`member-promote-yes-${m.user_id}`} onClick={() => promote(m)}
                        className="bg-forest hover:bg-forest-hover">
                        Make leader
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              {m.is_leader && userId === c.original_leader_user_id && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`member-demote-${m.user_id}`}
                      className="text-xs px-2.5 py-1.5 rounded-full border border-amber-200 text-amber-700 hover:bg-amber-50 transition-colors">
                      Change to member
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid={`demote-confirm-${m.user_id}`}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Change {m.name} to member?</AlertDialogTitle>
                      <AlertDialogDescription>
                        They'll stay in the collective but lose leader tools (editing the
                        collective, managing members, pinning messages). They'll be notified.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid={`member-demote-yes-${m.user_id}`} onClick={() => demote(m)}
                        className="bg-amber-600 hover:bg-amber-700">
                        Change to member
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              <button data-testid={`member-remove-${m.user_id}`} onClick={() => remove(m)}
                className="text-xs px-2.5 py-1.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50 transition-colors">
                Remove
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function fmtGatheringDate(isoStr) {
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })
      + " · " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch { return isoStr; }
}

function CreateGatheringDialog({ id, open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("10:30");
  const [saving, setSaving] = useState(false);
  // 18-B1 item 8: "Start from a template" — prefills the form; blank stays fine
  const [templates, setTemplates] = useState([]);
  const [usedTpl, setUsedTpl] = useState(null);
  useEffect(() => {
    if (open) api.gatheringTemplates().then(setTemplates).catch(() => {});
  }, [open]);
  const applyTemplate = (t) => {
    setTitle(t.prefill.title);
    setDescription(t.prefill.description);
    setTime(t.prefill.start_time || "10:30");
    setUsedTpl(t);
    toast.success(`${t.title} loaded — just pick a date and where`);
  };
  // 18-A2 item 3: campaign opt-in when the date falls in the active range
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [campaignOptIn, setCampaignOptIn] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.activeCampaign().then((r) => setActiveCampaign(r.campaign || null)).catch(() => {});
  }, [open]);
  const campaignEligible = Boolean(
    activeCampaign && date && date >= activeCampaign.starts_on && date <= activeCampaign.ends_on);

  const submit = async () => {
    if (!title.trim()) return toast.error("Give your gathering a name");
    if (!date) return toast.error("Pick a date");
    setSaving(true);
    try {
      await api.createGathering(id, {
        title: title.trim(), description: description.trim(),
        location: location.trim(), starts_at: `${date}T${time || "10:30"}:00`,
        campaign_id: campaignEligible && campaignOptIn ? activeCampaign.id : null,
      });
      toast.success("Gathering posted — members have been notified");
      setTitle(""); setDescription(""); setLocation(""); setDate("");
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create gathering");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="gathering-create-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-forest">New gathering</DialogTitle>
          <DialogDescription>
            A social get-together for your collective — no volunteering, no hours, just neighbours meeting neighbours.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {templates.length > 0 && (
            <div data-testid="gathering-template-gallery" className="bg-cream/60 border border-forest/10 rounded-xl px-3 py-2.5">
              <div className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold">Start from a template</div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {templates.map((t) => (
                  <button
                    key={t.slug}
                    data-testid={`gathering-template-${t.slug}`}
                    onClick={() => applyTemplate(t)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      usedTpl?.slug === t.slug
                        ? "border-forest bg-forest text-white"
                        : "border-forest/25 text-forest hover:bg-forest/5"
                    }`}
                  >
                    {t.title}
                  </button>
                ))}
              </div>
              {usedTpl && (
                <a
                  href={`/programme-guides/${usedTpl.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="gathering-template-guide-link"
                  className="inline-block text-xs text-forest underline underline-offset-2 mt-1.5"
                >
                  Print the {usedTpl.title} how-to guide (A5)
                </a>
              )}
            </div>
          )}
          <Input data-testid="gathering-title-input" placeholder="e.g. Sunday coffee morning" value={title} onChange={(e) => setTitle(e.target.value)} />
          <Textarea data-testid="gathering-desc-input" placeholder="What's the plan? (optional)" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          <Input data-testid="gathering-location-input" placeholder={usedTpl?.prefill?.location_hint || "Where? e.g. the corner café (optional)"} value={location} onChange={(e) => setLocation(e.target.value)} />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="gathering-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            <Input data-testid="gathering-time-input" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
          {campaignEligible && (
            <label className="flex items-center gap-2 text-sm text-forest cursor-pointer" data-testid="gathering-campaign-optin-row">
              <input type="checkbox" data-testid="gathering-campaign-optin" checked={campaignOptIn} onChange={(e) => setCampaignOptIn(e.target.checked)} />
              Part of {activeCampaign.name}
            </label>
          )}
          <Button data-testid="gathering-submit-btn" onClick={submit} disabled={saving} className="w-full rounded-full bg-forest hover:bg-forest-hover h-11">
            {saving ? "Posting…" : "Post gathering"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GatheringsTab({ id, isMember, isLeader, userId }) {
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);
  const [conflictData, setConflictData] = useState(null); // A1: {conflicts, severity, gathering}
  const load = () => api.listGatherings(id).then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps -- render-scoped fetch helper; effect keys on its real inputs

  const rsvp = async (g, going, party = 0) => {
    // A1: double-booking check before a NEW positive RSVP (party edits skip it).
    if (going && !g.going) {
      try {
        const c = await api.myConflicts({ starts_at: g.starts_at, exclude_id: g.id });
        if (c.conflicts?.length) return setConflictData({ ...c, gathering: g, party });
      } catch { /* never block the RSVP on the check itself */ }
    }
    await doRsvp(g, going, party);
  };

  const doRsvp = async (g, going, party = 0) => {
    setConflictData(null);
    try {
      const res = await api.rsvpGathering(g.id, going, party);
      if (going && !g.going) toast.success("You're going! 🎉");
      else if (!going) toast.success("RSVP removed");
      else toast.success(party === 0 ? "Just you — updated" : `Bringing +${party} — updated`);
      setItems((arr) => arr.map((x) => x.id === g.id ? { ...x, going: res.going, rsvp_count: res.rsvp_count, my_party: res.my_party } : x));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update RSVP"); }
  };

  const cancel = async (g) => {
    try {
      await api.cancelGathering(g.id);
      toast("Gathering cancelled — attendees notified");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); }
  };

  if (items === null) return <div className="text-sm text-neutral-500 py-8">Loading gatherings…</div>;

  return (
    <div className="space-y-4">
      <ConflictWarningDialog
        open={!!conflictData}
        onOpenChange={(o) => !o && setConflictData(null)}
        conflicts={conflictData?.conflicts || []}
        severity={conflictData?.severity}
        onConfirm={() => doRsvp(conflictData.gathering, true, conflictData.party || 0)}
        confirmLabel="RSVP anyway"
      />
      {isMember && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CalendarSubscribeLink testId="gatherings-subscribe-link" label="subscribe to all your commitments in your calendar app" />
          <Button data-testid="gathering-new-btn" onClick={() => setCreating(true)} className="rounded-full bg-terracotta hover:bg-terracotta/90 h-10 px-5 text-sm">
            <Plus className="w-4 h-4 mr-1.5" /> New gathering
          </Button>
        </div>
      )}
      {items.length === 0 ? (
        <div className="text-center py-12 bg-white border border-dashed border-neutral-300 rounded-2xl">
          <Coffee className="w-8 h-8 text-forest/30 mx-auto" />
          <div className="text-sm text-neutral-500 mt-2">
            No upcoming gatherings yet.{isMember ? " Host the first one!" : " Join the collective to host one."}
          </div>
        </div>
      ) : (
        <div data-testid="gatherings-list" className="space-y-3">
          {items.map((g) => (
            <div key={g.id} data-testid={`gathering-card-${g.id}`} className="bg-white border border-neutral-200 rounded-xl p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-terracotta bg-terracotta/10 rounded-full px-2.5 py-0.5">Social</span>
                    <span className="font-display text-lg text-forest">{g.title}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-sm text-neutral-600">
                    <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-forest/60" />{fmtGatheringDate(g.starts_at)}</span>
                    {g.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-forest/60" />{g.location}</span>}
                  </div>
                  {g.description && <p className="text-sm text-neutral-600 mt-2">{g.description}</p>}
                  <div className="text-xs text-neutral-500 mt-2 flex items-center gap-3">
                    <span>Hosted by {g.host_name}</span>
                    {!g.is_host && <ReportButton targetType="gathering" targetId={g.id} label="" />}
                  </div>
                </div>
                <div className="text-center shrink-0">
                  <div data-testid={`gathering-rsvp-count-${g.id}`} className="font-display text-2xl text-forest leading-none">{g.rsvp_count}</div>
                  <div className="text-[10px] uppercase tracking-wide text-neutral-500 mt-1">going</div>
                </div>
              </div>
              {isMember && Array.isArray(g.attendees) && g.attendees.length > 0 && (
                <div className="flex items-center gap-1.5 mt-3">
                  {g.attendees.slice(0, 8).map((a) => (
                    <Avatar key={a.user_id} className="w-7 h-7 border border-white -ml-1 first:ml-0">
                      <AvatarImage src={imgSrc(a.picture)} />
                      <AvatarFallback className="text-[10px] bg-forest/10 text-forest">{(a.name || "?")[0]}</AvatarFallback>
                    </Avatar>
                  ))}
                  {g.rsvp_count > 8 && <span className="text-xs text-neutral-500 ml-1">+{g.rsvp_count - 8} more</span>}
                </div>
              )}
              <div className="flex gap-2 mt-4">
                {isMember && !g.is_host && (
                  g.going ? (
                    <>
                      <Button data-testid={`gathering-unrsvp-${g.id}`} variant="outline" onClick={() => rsvp(g, false)} className="rounded-full h-9 px-4 text-sm">
                        Can't make it
                      </Button>
                      <select
                        data-testid={`gathering-party-${g.id}`}
                        value={g.my_party || 0}
                        onChange={(e) => rsvp(g, true, parseInt(e.target.value, 10))}
                        title="Bringing others?"
                        className="h-9 rounded-full border border-neutral-200 bg-white text-sm px-3 text-forest self-center"
                      >
                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                          <option key={n} value={n}>{n === 0 ? "Just me" : `+${n}`}</option>
                        ))}
                      </select>
                    </>
                  ) : (
                    <Button data-testid={`gathering-rsvp-${g.id}`} onClick={() => rsvp(g, true)} className="rounded-full bg-forest hover:bg-forest-hover h-9 px-4 text-sm">
                      <CheckCircle2 className="w-4 h-4 mr-1.5" /> I'm going
                    </Button>
                  )
                )}
                {g.is_host && <Badge variant="outline" className="rounded-full self-center">You're hosting</Badge>}
                {!isMember && <span className="text-xs text-neutral-500 self-center">Join the collective to RSVP</span>}
                {isMember && (
                  <a
                    data-testid={`gathering-ics-${g.id}`}
                    href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/gatherings/${g.id}/calendar.ics`}
                    download
                    title="Download .ics for this gathering"
                    className="inline-flex items-center gap-1 self-center text-xs px-3 py-1.5 rounded-full border border-forest/30 text-forest hover:bg-forest/5 transition-colors"
                  >
                    <CalendarPlus className="w-3.5 h-3.5" /> .ics
                  </a>
                )}
                <a
                  data-testid={`gathering-wa-share-${g.id}`}
                  href={`https://wa.me/?text=${encodeURIComponent(`"${g.title}" — a neighbourhood gathering${g.location ? ` at ${g.location}` : ""}. Details: ${api.shareUrl("gathering", g.id)}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Share via WhatsApp"
                  className="inline-flex items-center gap-1 self-center text-xs px-3 py-1.5 rounded-full border border-[#25D366]/40 text-[#128C7E] hover:bg-[#25D366]/10 transition-colors"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> Share
                </a>
                {(g.is_host || isLeader) && (
                  <Button data-testid={`gathering-cancel-${g.id}`} variant="ghost" onClick={() => cancel(g)} className="rounded-full h-9 px-3 text-sm text-red-500 hover:text-red-600 hover:bg-red-50 ml-auto">
                    <Trash2 className="w-4 h-4 mr-1" /> Cancel
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <CreateGatheringDialog id={id} open={creating} onClose={() => setCreating(false)} onCreated={load} />
    </div>
  );
}

function SettingsCard({ c, reload }) {
  // Leader-only (Phase 2): change membership mode post-approval. Switching
  // review -> open auto-admits everyone in the pending queue.
  const [saving, setSaving] = useState(false);
  const [waLink, setWaLink] = useState(c.whatsapp_link || "");
  useEffect(() => { setWaLink(c.whatsapp_link || ""); }, [c.whatsapp_link]);
  // 17-C item 6: attach/remove the group's WhatsApp invite link.
  const saveWaLink = async (value) => {
    setSaving(true);
    try {
      await api.updateCollectiveSettings(c.id, { whatsapp_link: value });
      toast.success(value ? "WhatsApp link attached — it now shows on your collective page" : "WhatsApp link removed");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update settings");
    } finally { setSaving(false); }
  };
  const setMode = async (mode) => {
    if (mode === c.membership_mode || saving) return;
    setSaving(true);
    try {
      const res = await api.updateCollectiveSettings(c.id, { membership_mode: mode });
      if (res.promoted_pending > 0) {
        toast.success(`Now open to join — ${res.promoted_pending} pending request${res.promoted_pending === 1 ? "" : "s"} auto-approved`);
      } else {
        toast.success(mode === "open" ? "Membership is now open to anyone in the area" : "New joiners will now need leader approval");
      }
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update settings");
    } finally { setSaving(false); }
  };
  const currentType = c.collective_type || "volunteering";
  const setType = async (t) => {
    if (t === currentType || saving) return;
    setSaving(true);
    try {
      await api.updateCollectiveSettings(c.id, { collective_type: t });
      toast.success("Collective type updated");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update settings");
    } finally { setSaving(false); }
  };
  return (
    <div data-testid="collective-settings-card" className="bg-white border border-neutral-200 rounded-xl p-6 space-y-3">
      <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold flex items-center gap-1.5">
        <Crown className="w-3.5 h-3.5 text-terracotta" /> Collective settings
      </div>
      <div>
        <div className="text-sm font-medium text-forest">Membership</div>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            data-testid="settings-mode-open"
            disabled={saving}
            onClick={() => setMode("open")}
            className={`flex-1 min-w-fit whitespace-nowrap text-xs px-2.5 py-2 rounded-lg border transition-colors ${c.membership_mode === "open"
              ? "border-forest bg-forest text-white"
              : "border-neutral-200 text-neutral-600 hover:border-forest hover:text-forest"}`}
          >
            Open to join
          </button>
          <button
            data-testid="settings-mode-review"
            disabled={saving}
            onClick={() => setMode("review")}
            className={`flex-1 min-w-fit whitespace-nowrap text-xs px-2.5 py-2 rounded-lg border transition-colors ${c.membership_mode === "review"
              ? "border-forest bg-forest text-white"
              : "border-neutral-200 text-neutral-600 hover:border-forest hover:text-forest"}`}
          >
            Request to join
          </button>
        </div>
        <p className="text-xs text-neutral-500 mt-2">
          {c.membership_mode === "review"
            ? "New joiners wait for a leader to approve them. Switching to open auto-approves anyone already waiting."
            : "Anyone within the area radius joins instantly."}
        </p>
      </div>
      <div>
        <div className="text-sm font-medium text-forest">Collective type</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {[["volunteering", "Volunteering"], ["social", "Social"], ["both", "Both"]].map(([v, l]) => (
            <button
              key={v}
              data-testid={`settings-type-${v}`}
              disabled={saving}
              onClick={() => setType(v)}
              className={`flex-1 min-w-fit whitespace-nowrap text-xs px-2.5 py-2 rounded-lg border transition-colors ${currentType === v
                ? "border-forest bg-forest text-white"
                : "border-neutral-200 text-neutral-600 hover:border-forest hover:text-forest"}`}
            >
              {l}
            </button>
          ))}
        </div>
        <p className="text-xs text-neutral-500 mt-2">
          Shown as a badge on your collective — helps neighbours know whether you organise volunteer work, social meetups, or a mix.
        </p>
      </div>
      <div>
        <div className="text-sm font-medium text-forest">WhatsApp group</div>
        <div className="mt-2 flex gap-2">
          <Input
            data-testid="settings-whatsapp-input"
            value={waLink}
            onChange={(e) => setWaLink(e.target.value)}
            placeholder="https://chat.whatsapp.com/…"
            className="h-9 text-xs"
          />
          <Button
            data-testid="settings-whatsapp-save"
            disabled={saving || (waLink.trim() === (c.whatsapp_link || ""))}
            onClick={() => saveWaLink(waLink.trim())}
            variant="outline"
            className="rounded-full h-9 px-4 text-xs shrink-0"
          >
            {c.whatsapp_link ? "Update" : "Attach"}
          </Button>
          {c.whatsapp_link && (
            <Button
              data-testid="settings-whatsapp-remove"
              disabled={saving}
              onClick={() => saveWaLink("")}
              variant="ghost"
              className="rounded-full h-9 px-3 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 shrink-0"
            >
              Remove
            </Button>
          )}
        </div>
        <p className="text-xs text-neutral-500 mt-2">
          Paste your group's invite link and a "Chat on WhatsApp" button appears on this page (behind a safety note). We never import members or read messages.
        </p>
      </div>
    </div>
  );
}


// Phase 17.5: member-facing "offer to lead" affordance for dormant collectives.
// Renders nothing unless the server says the collective is quiet AND the
// viewer is an eligible member (all rules re-enforced server-side on submit).
function TakeoverCard({ id, user, isMember, isLeader }) {
  const [ctx, setCtx] = useState(null);
  const [open, setOpen] = useState(false);
  const [motivation, setMotivation] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !isMember || isLeader) { setCtx(null); return undefined; }
    let cancelled = false;
    api.takeoverContext(id).then((r) => { if (!cancelled) setCtx(r); }).catch(() => {});
    return () => { cancelled = true; };
  }, [id, user?.user_id, isMember, isLeader]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ctx || !ctx.dormant || ctx.is_leader) return null;

  const submit = async () => {
    if (!motivation.trim()) return toast.error("Tell us briefly why you'd like to lead it");
    setBusy(true);
    try {
      const req = await api.requestTakeover(id, motivation.trim());
      setCtx((prev) => ({ ...prev, eligible: false, my_request: req }));
      setOpen(false);
      toast.success("Offer sent — our team will review it and let you know.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send your offer");
    }
    setBusy(false);
  };

  return (
    <div data-testid="takeover-card" className="bg-white border border-neutral-200 rounded-xl p-6 space-y-3">
      <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold inline-flex items-center gap-1.5">
        <Crown className="w-3.5 h-3.5 text-terracotta" /> Keep it going
      </div>
      {ctx.my_request ? (
        <p data-testid="takeover-pending" className="text-sm text-neutral-600">
          Your offer to lead is with our team — we'll let you know soon.
        </p>
      ) : (
        <>
          <p className="text-sm text-neutral-600">
            This collective seems inactive. If you'd like to step in and get
            things moving, you can offer to lead it — our team will review.
          </p>
          <Button
            data-testid="takeover-offer-btn"
            onClick={() => setOpen(true)}
            variant="outline"
            className="w-full rounded-full h-10 border-forest/30 text-forest hover:bg-forest/5"
          >
            Offer to lead this collective
          </Button>
        </>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="takeover-dialog">
          <DialogHeader>
            <DialogTitle>Offer to lead this collective</DialogTitle>
            <DialogDescription>
              It's been quiet here for a while. Tell us why you'd like to take
              the lead — an admin will review your offer. The current leader
              stays a member either way.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="takeover-motivation"
            value={motivation}
            onChange={(e) => setMotivation(e.target.value)}
            rows={3}
            placeholder="I'm local, I've got time, and I'd love to get gatherings going again…"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="takeover-submit" onClick={submit} disabled={busy} className="rounded-full bg-forest hover:bg-forest-hover">
              {busy ? "Sending…" : "Send offer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function LockedTab({ label }) {
  return (
    <div className="text-center py-12 bg-white border border-dashed border-neutral-300 rounded-2xl">
      <Lock className="w-8 h-8 text-forest/30 mx-auto" />
      <div className="text-sm text-neutral-500 mt-2">Join the collective to see the {label}.</div>
    </div>
  );
}

export default function CollectiveDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [chatUnread, setChatUnread] = useState(false);

  const load = () => api.getCollective(id).then(setC).catch(() => setNotFound(true));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps -- render-scoped fetch helper; effect keys on its real inputs

  // Chat-tab unread dot: cheap 30s poll of the rooms list (members only).
  // While the Chat tab is open, CollectiveChat reports fresher state directly.
  const isMemberNow = !!user && !!c?.members?.some((m) => m.user_id === user.user_id);
  useEffect(() => {
    if (!isMemberNow) return;
    let cancelled = false;
    const check = () => api.collectiveRooms(id)
      .then((rs) => { if (!cancelled) setChatUnread(rs.some((r) => r.unread)); })
      .catch(() => {});
    check();
    const t = setInterval(check, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [id, isMemberNow]);

  // 17-C item 6: complete a join that round-tripped through login/signup.
  useEffect(() => {
    if (!user || !c) return;
    let flag = null;
    try { flag = sessionStorage.getItem("civic_return_join"); } catch { /* ignore */ }
    if (flag !== id) return;
    try { sessionStorage.removeItem("civic_return_join"); } catch { /* ignore */ }
    const already = c.members.some((m) => m.user_id === user.user_id) ||
      (Array.isArray(c.pending_member_ids) && c.pending_member_ids.includes(user.user_id));
    if (already) return;
    if (user.lat == null || user.lng == null) {
      // 17-D carry-over: still no location (e.g. skipped onboarding) — route
      // into onboarding with the join intent restored, no raw server error.
      try { sessionStorage.setItem("civic_return_join", id); } catch { /* ignore */ }
      toast("Almost there — tell us where you are so we can connect you locally.");
      nav("/onboarding");
      return;
    }
    api.joinCollective(id)
      .then((res) => {
        if (res?.status === "pending") toast(res.message || "Request sent — a leader will review it.");
        else toast.success(`Welcome to ${c.name}!`);
        load();
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't finish joining — use the Join button."));
  }, [user?.user_id, c?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- one-shot flag consumption

  if (notFound) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">This collective doesn't exist or isn't live yet.</div>;
  if (!c) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">Loading…</div>;

  const isMember = !!user && c.members.some(m => m.user_id === user.user_id);
  const isLeader = !!user && c.leader_user_ids.includes(user.user_id);
  const leaders = c.members.filter(m => m.is_leader);
  const isReviewMode = c.membership_mode === "review";
  const isPending = !!user && Array.isArray(c.pending_member_ids) && c.pending_member_ids.includes(user.user_id);

  const join = async () => {
    if (!user) {
      // 17-C item 6: fast-join round trip — remember where they were headed,
      // send them through login/signup, then complete the join on return.
      try { sessionStorage.setItem("civic_return_join", id); } catch { /* ignore */ }
      return nav("/login");
    }
    // 17-D carry-over: collectives are area-gated server-side. A brand-new
    // account without a location gets routed into onboarding with the intent
    // preserved instead of hitting a raw server error.
    if (user.lat == null || user.lng == null) {
      try { sessionStorage.setItem("civic_return_join", id); } catch { /* ignore */ }
      toast("Almost there — tell us where you are so we can connect you locally.");
      return nav("/onboarding");
    }
    try {
      const res = await api.joinCollective(id);
      if (res?.status === "pending") {
        toast(res.message || "Request sent — a leader will review it.");
      } else {
        toast.success(`Welcome to ${c.name}!`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not join"); }
  };

  const leave = async () => {
    try {
      const res = await api.leaveCollective(id);
      if (res.deleted) { toast("Collective closed"); return nav("/collectives"); }
      toast("You left the collective");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not leave"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <Seo title={c.name} description={`${c.name} — a local collective covering ${c.area_label} (${c.radius_miles} mile radius) on CivicHearth.`} />
      <button onClick={() => nav("/collectives")} className="text-sm text-neutral-500 hover:text-forest inline-flex items-center gap-1.5">
        <ArrowLeft className="w-4 h-4" /> All collectives
      </button>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold inline-flex items-center gap-1.5">
            <MapPinned className="w-4 h-4" /> Collective
          </div>
          <div className="flex items-center gap-3 flex-wrap mt-2">
            <h1 className="font-display text-4xl text-forest tracking-tight">{c.name}</h1>
            <CollectiveTypeBadge type={c.collective_type} testId={`collective-type-badge-${c.id}`} />
          </div>
          <div className="flex flex-wrap gap-3 mt-3 text-sm text-neutral-600">
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-forest/60" />{c.area_label} · {c.radius_miles} mile radius</span>
            <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4 text-forest/60" />{c.member_count} member{c.member_count !== 1 ? "s" : ""}</span>
          </div>
          {leaders.length > 0 && (
            <div className="text-sm text-neutral-600 mt-2 inline-flex items-center gap-1.5">
              <Crown className="w-4 h-4 text-terracotta" /> Led by {leaders.map(l => l.name).join(", ")}
            </div>
          )}
          <p className="text-neutral-700 leading-relaxed mt-5 whitespace-pre-line">{c.description}</p>
          {user && !isLeader && (
            <div className="mt-2">
              <ReportButton targetType="collective" targetId={c.id} label="Report this collective" />
            </div>
          )}

          <Tabs defaultValue={isMember ? "board" : "local"} className="mt-8">
            <TabsList className="rounded-full bg-forest/5 flex-wrap h-auto">
              <TabsTrigger data-testid="tab-noticeboard" value="board" className="rounded-full"><Megaphone className="w-3.5 h-3.5 mr-1.5" /> Noticeboard</TabsTrigger>
              <TabsTrigger data-testid="tab-chat" value="chat" className="rounded-full">
                <MessagesSquare className="w-3.5 h-3.5 mr-1.5" /> Chat
                {chatUnread && <span data-testid="chat-tab-unread-dot" className="inline-block w-1.5 h-1.5 rounded-full bg-terracotta ml-1.5" />}
              </TabsTrigger>
              <TabsTrigger data-testid="tab-local" value="local" className="rounded-full"><Sprout className="w-3.5 h-3.5 mr-1.5" /> Local activity</TabsTrigger>
              <TabsTrigger data-testid="tab-gatherings" value="gatherings" className="rounded-full"><Coffee className="w-3.5 h-3.5 mr-1.5" /> Gatherings</TabsTrigger>
              <TabsTrigger data-testid="tab-members" value="members" className="rounded-full"><Users className="w-3.5 h-3.5 mr-1.5" /> Members</TabsTrigger>
              {isLeader && (
                <TabsTrigger data-testid="tab-stats" value="stats" className="rounded-full"><BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Stats</TabsTrigger>
              )}
            </TabsList>
            <TabsContent value="board" className="mt-5">
              {isMember ? <Noticeboard id={id} isLeader={isLeader} userId={user?.user_id} /> : <LockedTab label="noticeboard" />}
            </TabsContent>
            <TabsContent value="chat" className="mt-5">
              {isMember ? <CollectiveChat id={id} isLeader={isLeader} /> : <LockedTab label="chat" />}
            </TabsContent>
            <TabsContent value="local" className="mt-5">
              <LocalActivity id={id} isMember={isMember} />
            </TabsContent>
            <TabsContent value="gatherings" className="mt-5">
              <GatheringsTab id={id} isMember={isMember} isLeader={isLeader} userId={user?.user_id} />
            </TabsContent>
            <TabsContent value="members" className="mt-5 space-y-5">
              {isLeader && isReviewMode && <PendingRequests id={id} reload={load} />}
              <MembersList c={c} isLeader={isLeader} userId={user?.user_id} reload={load} />
            </TabsContent>
            {isLeader && (
              <TabsContent value="stats" className="mt-5">
                <StatsTab id={id} />
              </TabsContent>
            )}
          </Tabs>
        </div>

        <div className="space-y-4">
          <MapLeaflet markers={[{ lat: c.lat, lng: c.lng, title: c.name, subtitle: c.area_label }]} center={[c.lat, c.lng]} zoom={11} height={240} />
          <ShareButton
            testId="collective-share"
            className="w-full rounded-full"
            imageUrl={api.ogCollectiveUrl(c.id)}
            shareUrl={api.shareUrl("collective", c.id)}
            title={`${c.name} on CivicHearth`}
            text={`${c.name} — our local collective around ${c.area_label}. Join your neighbours.`}
            waText={`Our street is organising — join ${c.name} on CivicHearth:`}
            filename="civichearth-collective.png"
            label="Share this collective"
          />
          <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-3">
            <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">
              {isMember ? "You're a member" : "Live in the area?"}
            </div>
            {!isMember ? (
              isPending ? (
                <Button data-testid="request-pending-btn" disabled className="w-full rounded-full h-11 bg-amber-500/80 text-white cursor-default">
                  Request pending review
                </Button>
              ) : (
                <Button data-testid="join-collective-btn" onClick={join} className="w-full rounded-full h-11 bg-terracotta hover:bg-terracotta-hover">
                  {isReviewMode ? "Request to join" : "Join this collective"}
                </Button>
              )
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid="leave-collective-btn" variant="outline" className="w-full rounded-full h-11 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
                    Leave collective
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="leave-collective-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Leave {c.name}?</AlertDialogTitle>
                    <AlertDialogDescription>You can rejoin any time if you're still in the area.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="leave-collective-no">Stay</AlertDialogCancel>
                    <AlertDialogAction data-testid="leave-collective-yes" onClick={leave} className="bg-red-600 hover:bg-red-700">Yes, leave</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <p className="text-xs text-neutral-500">
              {isReviewMode
                ? `Requests reviewed by a leader. Radius: ${c.radius_miles} miles around ${c.area_label}.`
                : `Membership is open to anyone within ${c.radius_miles} miles of ${c.area_label}.`}
            </p>
          </div>
          <WhatsAppChatCard c={c} user={user} />
          {isMember && !isLeader && <TakeoverCard id={id} user={user} isMember={isMember} isLeader={isLeader} />}
          {isLeader && <SettingsCard c={c} reload={load} />}
          {isLeader && <ProgrammeIdeasCard />}
          {isLeader && <InviteStreetCard c={c} />}
          {isLeader && <InviteCard id={id} />}
        </div>
      </div>
    </div>
  );
}
